import React, { useState, useEffect } from "react";
import { Crown, X, CheckCircle, Sparkle } from "@phosphor-icons/react";
import api from "../lib/api";
import { applyCurrencyMethodGate, createRazorpayCheckout } from "../lib/razorpayOptions";
import { useCurrency } from "../context/CurrencyContext";
import { toast } from "sonner";

/**
 * VipKingOffer — full-screen modal shown when a chat ends.
 * Pitches the ₹4999 → ₹1500 wallet + 7-day premium-tool unlock pack and
 * handles the Razorpay checkout flow.
 */
export default function VipKingOffer({ open, onClose, onSuccess, user }) {
  const [busy, setBusy] = useState(false);
  const [pack, setPack] = useState(null);
  const { showingUsd, formatPrice } = useCurrency();

  useEffect(() => {
    if (!open) return;
    api.get("/wallet/vip-king/pack").then((r) => setPack(r.data)).catch(() => {});
  }, [open]);

  const toolCount = pack?.tool_count ?? 24;
  const toolsWorth = Math.floor((pack?.tools_total_worth ?? 33361) / 1000) * 1000;

  if (!open) return null;

  // Don't offer VIP to users who already have VIP credit remaining
  if (user?.is_vip) return null;

  const startCheckout = async () => {
    setBusy(true);
    try {
      // Ensure Razorpay script is loaded
      await loadRazorpayScript();
      // 1. Create VIP order on backend — passes display_currency so US users
      //    get a native USD Razorpay popup (and PayPal charges in USD).
      const { data: order } = await api.post("/wallet/vip-king/order", {
        display_currency: showingUsd ? "USD" : "INR",
      });
      const symbol = order.currency === "USD" ? "$" : "₹";
      // 2. Open Razorpay checkout
      const rzp = createRazorpayCheckout({
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency || "INR",
        order_id: order.order_id,
        name: "AstroKuber · VIP King",
        description: `${symbol}${order.amount} — ₹1500 wallet credit + unlock all premium tools for 7 days`,
        prefill: order.prefill,
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            const { data } = await api.post("/wallet/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(
              `Congratulations, VIP King! ${formatPrice(data.credited || 1500)} credited.`,
              { duration: 6000 },
            );
            onSuccess?.(data);
            onClose?.();
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment couldn't be verified.");
          }
        },
        modal: {
          ondismiss: () => setBusy(false),
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-6 animate-[fadeIn_240ms_ease-out]"
      data-testid="vip-king-offer"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full sm:max-w-md bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] rounded-t-3xl sm:rounded-3xl shadow-2xl border border-amber-700/40 overflow-hidden relative"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 transition-colors z-10"
          aria-label="Close VIP offer"
          data-testid="vip-king-offer-close"
        >
          <X size={20} />
        </button>

        {/* Crown decoration */}
        <div className="px-6 pt-8 pb-2 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-xl mb-4 animate-[crownPulse_2.4s_ease-in-out_infinite]">
            <Crown size={42} weight="fill" className="text-[#3F2D1B]" />
          </div>
          <div className="text-amber-300 text-[10px] uppercase tracking-[0.25em] mb-2">
            One-Time Offer · Just for you
          </div>
          <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">
            Become a <span className="text-amber-300">VIP King</span>
          </h2>
        </div>

        {/* Headline pricing */}
        <div className="px-6 py-5 text-center">
          <div className="flex items-end justify-center gap-2 mb-1">
            <span className="text-5xl font-serif-display text-amber-300" data-testid="vip-king-price">{formatPrice(4999)}</span>
            <span className="text-amber-200/80 text-xs pb-2 font-medium">+ {showingUsd ? "Taxes" : "GST"} (18%)</span>
          </div>
          <div className="text-sm text-amber-100/80">
            {formatPrice(1500)} wallet credit + every premium tool unlocked for <span className="font-bold text-[#FAF4E8]">7 days</span>
          </div>
          <div className="text-[10px] text-amber-200/60 mt-1.5" data-testid="vip-king-total">
            Total {formatPrice(Math.round(4999 * 1.18))} · incl. 18% {showingUsd ? "taxes" : "GST"}
          </div>
          <div className="mt-2 inline-block px-3 py-1 rounded-full bg-rose-500/20 border border-rose-400/40 text-rose-200 text-[11px] font-bold">
            UNLOCK ALL {toolCount} PREMIUM TOOLS · WORTH {formatPrice(toolsWorth)}+
          </div>
        </div>

        {/* Benefits */}
        <div className="px-6 py-4 space-y-3">
          <Benefit text={`Unlock all ${toolCount} premium tools for the next 7 days — Varshaphal, D9 Navamsa, Deep Numerology, Bhrigu Bindu, Chakra Heat-Map, Nakshatra, Compatibility & more`} />
          <Benefit text={`Instant ${formatPrice(1500)} wallet credit to chat with any astrologer`} />
          <Benefit text="VIP King crown badge on your profile & in every chat" />
          <Benefit text="Recognised as a royal seeker by every astrologer you chat with" />
          <Benefit text={`Crown stays with you until your ${formatPrice(1500)} credit is consumed`} />
        </div>

        {/* CTA */}
        <div className="px-6 pb-6 pt-2 space-y-2.5">
          <button
            onClick={startCheckout}
            disabled={busy}
            className="w-full py-4 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-[#1A1410] font-bold text-base shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2"
            data-testid="vip-king-checkout-btn"
          >
            <Crown size={18} weight="fill" />
            {busy ? "Opening checkout..." : `Become a VIP King · Pay ${formatPrice(Math.round(4999 * 1.18))}`}
          </button>
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-full text-amber-200/70 hover:text-amber-100 text-sm font-medium transition-colors"
            data-testid="vip-king-skip-btn"
          >
            Maybe later
          </button>
          <p className="text-amber-200/50 text-[10px] text-center font-light tracking-wide">
            Secured by Razorpay · UPI · Cards · Netbanking · Wallets
          </p>
        </div>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes crownPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(245, 200, 100, 0.6); }
          50% { transform: scale(1.05); box-shadow: 0 0 0 18px rgba(245, 200, 100, 0); }
        }
      `}</style>
    </div>
  );
}

function Benefit({ text }) {
  return (
    <div className="flex items-start gap-2.5">
      <CheckCircle size={18} weight="fill" className="text-amber-300 flex-shrink-0 mt-0.5" />
      <span className="text-sm text-amber-50/95 leading-snug">{text}</span>
    </div>
  );
}

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load Razorpay"));
    document.body.appendChild(s);
  });
}
